import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { candidatesAPI, intelligenceAPI, offersAPI } from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import VerificationSendDialog from '../components/VerificationSendDialog';
import { CandidateJourney } from '../components/CandidateJourney';
import CandidateHeader from '../components/candidate/CandidateHeader';
import CandidateProfileTabs from '../components/candidate/CandidateProfileTabs';
import CandidateAssessmentsTab from '../components/candidate/CandidateAssessmentsTab';
import CandidateInterviewsTab from '../components/candidate/CandidateInterviewsTab';
import CandidatePipelineTab from '../components/candidate/CandidatePipelineTab';
import CandidateIntelligenceTab from '../components/candidate/CandidateIntelligenceTab';
import CandidateBehavioralTab from '../components/candidate/CandidateBehavioralTab';
import { getScoreClass, getScoreColor } from '../lib/scoreUtils';

export default function CandidateDetailPage() {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [depthAnalysis, setDepthAnalysis] = useState(null);
  const [analyzingDepth, setAnalyzingDepth] = useState(false);
  const [depthError, setDepthError] = useState(null);
  const [offerProb, setOfferProb] = useState(null);
  const [pipelineHistory, setPipelineHistory] = useState([]);
  const [offer, setOffer] = useState(null);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [offerForm, setOfferForm] = useState({ ctc: '', currency: 'USD', joining_date: '', notes: '' });
  const [onboardingPlan, setOnboardingPlan] = useState(null);

  const loadCandidate = useCallback(async () => {
    try {
      const res = await candidatesAPI.get(candidateId);
      setCandidate(res.data);
      // Load existing depth analysis if available
      if (res.data.depth_analysis) {
        setDepthAnalysis(res.data.depth_analysis);
      }
      // Load pipeline history + offer + probability
      try {
        const [histRes, offerRes, probRes] = await Promise.all([
          offersAPI.getPipelineHistory(candidateId),
          offersAPI.getForCandidate(candidateId),
          offersAPI.getOfferProbability(candidateId),
        ]);
        setPipelineHistory(histRes.data);
        setOffer(offerRes.data);
        setOfferProb(probRes.data);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.debug('Optional candidate offer/pipeline data unavailable:', e?.message);
      }
    } catch (err) {
      toast.error('Failed to load candidate');
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => {
    loadCandidate();
  }, [loadCandidate]);

  const runDepthAnalysis = async () => {
    setAnalyzingDepth(true);
    setDepthError(null);
    try {
      const res = await intelligenceAPI.analyzeDepth(candidateId);
      setDepthAnalysis(res.data);
      toast.success('Resume depth analysis complete!', { duration: 10000 });
    } catch (err) {
      let msg = err.response?.data?.detail || err.message || 'Analysis failed';
      if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        msg = 'Analysis is taking longer than expected. Please wait 30 seconds and click Re-analyze — results will load instantly from cache.';
      }
      setDepthError(msg);
      toast.error(msg, { duration: 15000 });
    } finally {
      setAnalyzingDepth(false);
    }
  };

  // O(1) skill lookup set — avoids repeated Array.includes() during skills render
  const matchedSkillsSet = useMemo(
    () => new Set(candidate?.match_result?.matched_skills || []),
    [candidate]
  );

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'hsl(var(--muted-foreground))' }}>Loading candidate...</div>;
  }

  if (!candidate) {
    return <div style={{ textAlign: 'center', padding: '4rem', color: 'hsl(var(--muted-foreground))' }}>Candidate not found</div>;
  }

  const parsed = candidate.parsed_resume || {};
  const match = candidate.match_result || {};
  const scoreBreakdown = match.score_breakdown || {};

  return (
    <div data-testid="candidate-detail-page">
      <CandidateHeader
        candidate={candidate}
        candidateId={candidateId}
        parsed={parsed}
        onBack={() => navigate(-1)}
        onVerify={() => setShowVerifyDialog(true)}
        loadCandidate={loadCandidate}
        analyzingDepth={analyzingDepth}
        depthError={depthError}
        onAnalyze={runDepthAnalysis}
        onClearError={() => setDepthError(null)}
      />

      {candidate.journey && (
        <div style={{ marginBottom: '1.5rem' }}>
          <CandidateJourney journey={candidate.journey} />
        </div>
      )}


      {candidate.status !== 'completed' && (
        <Card style={{ background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.2)', borderRadius: 12, padding: '1rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span className="status-dot status-processing" />
          <span style={{ fontSize: '0.875rem', color: '#D97706' }}>
            {candidate.status === 'error'
              ? `Resume processing failed (Error: ${candidate.error_message})`
              : `Resume is being ${{ uploaded: 'uploaded', parsing: 'parsed', matching: 'matched' }[candidate.status] || 'processed'}...`}
          </span>
        </Card>
      )}

      <Tabs defaultValue="overview" data-testid="score-breakdown-panel">
        <TabsList style={{ background: 'hsl(var(--card))', borderRadius: 10, marginBottom: '1.5rem' }}>
          <TabsTrigger value="overview" data-testid="score-breakdown-tab" style={{ borderRadius: 8 }}>Overview</TabsTrigger>
          <TabsTrigger value="skills" data-testid="score-breakdown-tab" style={{ borderRadius: 8 }}>Skills</TabsTrigger>
          <TabsTrigger value="experience" data-testid="score-breakdown-tab" style={{ borderRadius: 8 }}>Experience</TabsTrigger>
          <TabsTrigger value="education" data-testid="score-breakdown-tab" style={{ borderRadius: 8 }}>Education</TabsTrigger>
          <TabsTrigger value="intelligence" style={{ borderRadius: 8 }}>Resume Intelligence</TabsTrigger>
          <TabsTrigger value="assessments" style={{ borderRadius: 8 }}>Assessments ({(candidate.assessment_attempts || []).length})</TabsTrigger>
          <TabsTrigger value="interviews" style={{ borderRadius: 8 }}>Interviews ({(candidate.interview_sessions || []).length})</TabsTrigger>
          <TabsTrigger value="behavioral" data-testid="candidate-behavioral-tab" style={{ borderRadius: 8 }}>Behavioral{candidate.behavioral?.status === 'completed' ? ' ✓' : ''}</TabsTrigger>
          <TabsTrigger value="pipeline" style={{ borderRadius: 8 }}>Pipeline & Offer</TabsTrigger>
        </TabsList>

        <CandidateProfileTabs candidate={candidate} candidateId={candidateId} parsed={parsed} match={match} scoreBreakdown={scoreBreakdown} matchedSkillsSet={matchedSkillsSet} depthAnalysis={depthAnalysis} analyzingDepth={analyzingDepth} runDepthAnalysis={runDepthAnalysis} offerProb={offerProb} pipelineHistory={pipelineHistory} offer={offer} showOfferForm={showOfferForm} setShowOfferForm={setShowOfferForm} offerForm={offerForm} setOfferForm={setOfferForm} onboardingPlan={onboardingPlan} setOnboardingPlan={setOnboardingPlan} loadCandidate={loadCandidate} />
        <CandidateAssessmentsTab candidate={candidate} candidateId={candidateId} parsed={parsed} match={match} scoreBreakdown={scoreBreakdown} matchedSkillsSet={matchedSkillsSet} depthAnalysis={depthAnalysis} analyzingDepth={analyzingDepth} runDepthAnalysis={runDepthAnalysis} offerProb={offerProb} pipelineHistory={pipelineHistory} offer={offer} showOfferForm={showOfferForm} setShowOfferForm={setShowOfferForm} offerForm={offerForm} setOfferForm={setOfferForm} onboardingPlan={onboardingPlan} setOnboardingPlan={setOnboardingPlan} loadCandidate={loadCandidate} />
        <CandidateInterviewsTab candidate={candidate} candidateId={candidateId} parsed={parsed} match={match} scoreBreakdown={scoreBreakdown} matchedSkillsSet={matchedSkillsSet} depthAnalysis={depthAnalysis} analyzingDepth={analyzingDepth} runDepthAnalysis={runDepthAnalysis} offerProb={offerProb} pipelineHistory={pipelineHistory} offer={offer} showOfferForm={showOfferForm} setShowOfferForm={setShowOfferForm} offerForm={offerForm} setOfferForm={setOfferForm} onboardingPlan={onboardingPlan} setOnboardingPlan={setOnboardingPlan} loadCandidate={loadCandidate} />
        <CandidatePipelineTab candidate={candidate} candidateId={candidateId} parsed={parsed} match={match} scoreBreakdown={scoreBreakdown} matchedSkillsSet={matchedSkillsSet} depthAnalysis={depthAnalysis} analyzingDepth={analyzingDepth} runDepthAnalysis={runDepthAnalysis} offerProb={offerProb} pipelineHistory={pipelineHistory} offer={offer} showOfferForm={showOfferForm} setShowOfferForm={setShowOfferForm} offerForm={offerForm} setOfferForm={setOfferForm} onboardingPlan={onboardingPlan} setOnboardingPlan={setOnboardingPlan} loadCandidate={loadCandidate} />
        <CandidateIntelligenceTab candidate={candidate} candidateId={candidateId} parsed={parsed} match={match} scoreBreakdown={scoreBreakdown} matchedSkillsSet={matchedSkillsSet} depthAnalysis={depthAnalysis} analyzingDepth={analyzingDepth} depthError={depthError} setDepthError={setDepthError} runDepthAnalysis={runDepthAnalysis} offerProb={offerProb} pipelineHistory={pipelineHistory} offer={offer} showOfferForm={showOfferForm} setShowOfferForm={setShowOfferForm} offerForm={offerForm} setOfferForm={setOfferForm} onboardingPlan={onboardingPlan} setOnboardingPlan={setOnboardingPlan} loadCandidate={loadCandidate} />
        <CandidateBehavioralTab candidate={candidate} candidateId={candidateId} parsed={parsed} loadCandidate={loadCandidate} />
      </Tabs>

      {/* Verification Dialog */}
      <VerificationSendDialog
        open={showVerifyDialog}
        onClose={() => setShowVerifyDialog(false)}
        candidate={candidate}
        jobId={candidate?.job_id}
      />
    </div>
  );
}
